import React from 'react';
import { Menu, MenuItem, ListItemIcon, Divider, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DeleteIcon from '@mui/icons-material/Delete';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadIcon from '@mui/icons-material/Upload';
import ShareIcon from '@mui/icons-material/Share';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useTranslation } from 'react-i18next';
import { DriveFile, isEditableMime } from '../services/myDriveService';

export type CtxAction = 'preview' | 'download' | 'edit-online' | 'rename' | 'copy' | 'move' | 'delete' | 'newFolder' | 'upload' | 'share';

interface Props {
  open: boolean;
  anchorPos: { top: number; left: number } | null;
  file: DriveFile | null;
  readonly: boolean;
  canShare?: boolean;   // 無 ee_drive_share:create → 分享項 disabled + tooltip（免 click→403 誤會系統壞）
  onAction: (action: CtxAction) => void;
  onClose: () => void;
}

export default function FileContextMenu({ open, anchorPos, file, readonly, canShare = true, onAction, onClose }: Props) {
  const { t } = useTranslation();

  return (
    <Menu
      open={open && !!anchorPos}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPos ?? undefined}
    >
      {file ? [
        !file.is_directory && (
          <MenuItem key="preview" onClick={() => { onAction('preview'); onClose(); }}>
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            {t('myDrive.preview', '預覽')}
          </MenuItem>
        ),
        !file.is_directory && (
          <MenuItem key="download" onClick={() => { onAction('download'); onClose(); }}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            {t('myDrive.download', '下載')}
          </MenuItem>
        ),
        !file.is_directory && isEditableMime(file.mime_type) && (
          <MenuItem key="edit-online" onClick={() => { onAction('edit-online'); onClose(); }}>
            <ListItemIcon><EditNoteIcon fontSize="small" /></ListItemIcon>
            {t('myDrive.editOnline', '線上編輯')}
          </MenuItem>
        ),
        !readonly && (
          <MenuItem key="rename" onClick={() => { onAction('rename'); onClose(); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            {t('myDrive.rename', '重新命名')}
          </MenuItem>
        ),
        // 複製：folder 灰掉（MVP 不支援 folder 複製）
        !readonly && (
          <MenuItem key="copy" onClick={() => { onAction('copy'); onClose(); }}
                    disabled={file.is_directory}>
            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
            {t('myDrive.copy', '複製')}
          </MenuItem>
        ),
        // 移動：folder 可用（cascade rename 已支援）
        !readonly && (
          <MenuItem key="move" onClick={() => { onAction('move'); onClose(); }}>
            <ListItemIcon><DriveFileMoveIcon fontSize="small" /></ListItemIcon>
            {t('myDrive.move', '移動')}
          </MenuItem>
        ),
        // 分享（#24 · Phase 2.5：檔案 + 資料夾皆可分享）。無權限 → disabled + tooltip（span 讓 disabled 也能 hover 出提示）
        (
          <Tooltip key="share"
            title={canShare ? '' : t('driveShare.noPermissionShare', '您無權限使用分享功能 · 請聯絡管理員開啟角色權限')}>
            <span>
              <MenuItem disabled={!canShare}
                onClick={() => { if (canShare) { onAction('share'); onClose(); } }}>
                <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
                {t('driveShare.share', '分享')}
              </MenuItem>
            </span>
          </Tooltip>
        ),
        !readonly && <Divider key="div" />,
        !readonly && (
          <MenuItem key="delete" onClick={() => { onAction('delete'); onClose(); }}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            {t('myDrive.delete', '刪除')}
          </MenuItem>
        ),
      ] : [
        !readonly && (
          <MenuItem key="newFolder" onClick={() => { onAction('newFolder'); onClose(); }}>
            <ListItemIcon><CreateNewFolderIcon fontSize="small" /></ListItemIcon>
            {t('myDrive.newFolder', '新增資料夾')}
          </MenuItem>
        ),
        !readonly && (
          <MenuItem key="upload" onClick={() => { onAction('upload'); onClose(); }}>
            <ListItemIcon><UploadIcon fontSize="small" /></ListItemIcon>
            {t('myDrive.selectFiles', '選擇檔案')}
          </MenuItem>
        ),
      ]}
    </Menu>
  );
}
